import { Routes } from '.generated/templates/Project.Routes.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { TextField } from '@sitecore-jss/sitecore-jss-react';
import classnames from 'classnames';
import Button from 'components/helpers/Button';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import useDictionary from 'lib/sitecore/use-dictionary';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';

import { OnLinkDataLayerPushProps } from 'lib/util/gtm-datalayer';

type DocumentItemProps = Sitecore.Override.ItemEx & {
  fields?: {
    Title?: TextField;
    Extension?: TextField;
  };
};
type ProductDetailPageProps = Routes.Fields.ProductDetailsPage & {
  fields: {
    otherLanguageDocuments?: DocumentItemProps[];
  };
};

type AppStoreGtmPushProps = OnLinkDataLayerPushProps & {
  productTitle: string;
};

/**
 * Handle app store data layer pushes
 */
export const appStoreDataLayerPush = ({
  productTitle,
  linkField,
  dataLayerInfo,
}: AppStoreGtmPushProps): void => {
  if (!linkField || !dataLayerInfo) {
    // Returning true means standard link click data layer reporting occurs
    return;
  }
  const myDataLayerInfo = dataLayerInfo;
  myDataLayerInfo.event = 'e_fileDownload';
  myDataLayerInfo.fileName = productTitle;
};

const OtherProducts = ({ fields }: ProductDetailPageProps): React.JSX.Element => {
  const dictionary = useDictionary();

  const appleAppstoreImage = {
    value: {
      src: '/-/media/project/nmdp/networkresourcecenter/images/appstore.jpg',
      alt: 'apple',
      height: '41',
      width: '125',
    },
  };
  const googlePlaystoreImage = {
    value: {
      src: '/-/media/project/nmdp/networkresourcecenter/images/playstore.jpg',
      alt: 'apple',
      height: '41',
      width: '132',
    },
  };

  return (
    <>
      <div className={classnames('mt-[10px]')}>
        <h3 className="mb-[16px] text-2xl font-medium leading-9 text-blue-navy">
          {dictionary.ProductDetails_Other_Formats}
        </h3>
        {fields?.otherLanguageDocuments && (
          <ul className={classnames('flex', 'flex-wrap')}>
            {fields?.otherLanguageDocuments.map((item) => {
              const link = item as DocumentItemProps;
              const title = link.fields.Title?.value as string;
              const fileExtension = (link.fields.Extension?.value as string).toUpperCase();
              return (
                <li
                  key={link.id}
                  className={classnames(
                    'max-w-[288px]',
                    'w-full',
                    'flex-auto',
                    'md:w-1/2',
                    'mb-[16px]'
                  )}
                >
                  <div className={classnames('text-body', 'font-medium', 'leading-4')}>
                    <Button
                      variant="link"
                      className={classnames('text-blue', 'hover:pointer')}
                      linkField={{
                        value: {
                          text: title,
                          href: link.url,
                          linktype: 'media',
                        },
                      }}
                    />{' '}
                    ({fileExtension})
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className={classnames('mb-[18px]')}>
          {fields?.productAppleStoreLink?.value?.href !== '' && (
            <span className={classnames('inline-block', 'mr-[23px]')}>
              <LinkA11yWrapper
                className=""
                editable={false}
                field={fields?.productAppleStoreLink}
                rel="noreferrer noopener"
                suppressLinkText
                suppressNewTabIcon
                onGtmPush={(props) =>
                  appStoreDataLayerPush({
                    ...props,
                    productTitle: fields?.productTitle?.value,
                  })
                }
              >
                <ImageWrapper field={appleAppstoreImage} />
              </LinkA11yWrapper>
            </span>
          )}
          {fields?.productPlayStoreLink?.value?.href !== '' && (
            <span className={classnames('inline-block', 'mr-[23px]')}>
              <LinkA11yWrapper
                className=""
                editable={false}
                field={fields?.productPlayStoreLink}
                rel="noreferrer noopener"
                suppressLinkText
                suppressNewTabIcon
                onGtmPush={(props) =>
                  appStoreDataLayerPush({
                    ...props,
                    productTitle: fields?.productTitle?.value,
                  })
                }
              >
                <ImageWrapper field={googlePlaystoreImage} />
              </LinkA11yWrapper>
            </span>
          )}
        </div>
        <div>
          {fields?.productAppleStoreLink.value.href !== '' && (
            <p className={classnames('text-sm', 'leading-2')}>{dictionary.AppleStore_Disclaimer}</p>
          )}
          {fields?.productPlayStoreLink.value.href !== '' && (
            <p className={classnames('text-sm', 'leading-2')}>{dictionary.PlayStore_Disclaimer}</p>
          )}
        </div>
      </div>
    </>
  );
};
export default OtherProducts;
